import { Icons } from '@cast/design-system';
import {
  ClusterProvider,
  isKentEligibleCluster,
  isKentManagedCluster,
  useClusters,
} from '@cast/shared-cluster-context-data-access';
import { useConsoleAccess } from '@cast/shared-trial-ui';
import { K8sProvider } from '@cast/shared-types';
import { Cluster } from '@cast/types';
import { usePath } from '@cast/utils';

import { TourKeys } from '@cast/constants';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { useAbility } from '@cast/shared-ability-data-access';
import { useIsTourActive } from 'core/tour-v2/hooks/useIsTourActive';
import { isDisconnectedForMonth } from 'features/cluster';
import { useOmniAvailableRegionsFeatureEnabled } from 'features/cluster/autoscaler/edge-locations/hooks/useOmniAvailableRegionsFeatureEnabled';
import { makeContainerLiveMigrationLink } from 'features/cluster/container-live-migration/navigation';
import { useStorageOptimizationAbilities } from 'features/cluster/storage-optimization/hooks/useStorageOptimizationAbilities';
import {
  makeKarpenterSettingsLink,
  makeKentGetStartedLink,
  makeKentSettingsLink,
} from 'features/kent/navigation';
import { useClusterSecurityFeatures } from 'features/organization/security-insights/hooks/useClusterSecurityFeatures';
import { useIsClusterSecurityAgentInstalled } from 'features/organization/security-insights/hooks/useIsClusterSecurityAgentInstalled';
import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';
import { makeNavigationLinkToAutoscalerSettings } from 'features-op/cluster/settings-op/navigation';

import { SidebarClusterSection } from '../_components/SidebarClusterSection';
import { SidebarItem } from '../_components/SidebarItem';
import { SidebarItemBadge } from '../_components/SidebarItemBadge';
import { SidebarLayout } from '../_components/SidebarLayout';
import { SidebarOrgSection } from '../_components/SidebarOrgSection';
import { useRebalancingPlanSidebarItemProps } from './_hooks/useRebalancingPlanSidebarItemProps';
import { CostMonitoringClusterSidebarItems } from './_items/CostMonitoringClusterSidebarItems';
import { CostMonitoringOrgSidebarItems } from './_items/CostMonitoringOrgSidebarItems';
import { SavingsSidebarItem } from './_items/SavingsSidebarItem';
import { SettingsSidebarItem } from './_items/SettingsSidebarItem';

const ClusterAutomationSidebar = ({ cluster }: { cluster: Cluster }) => {
  const basePath = PRODUCT_BASE_PATHS.automation;
  const containerLiveMigrationLink = usePath(() =>
    makeContainerLiveMigrationLink({ clusterId: cluster.id })
  );
  const isDisconnected = isDisconnectedForMonth(cluster);
  const isKentEligible = isKentEligibleCluster(cluster);
  const isOpTourActive = useIsTourActive(TourKeys.OpinionatedPlatform);
  const rebalancingPlanSidebarItemProps =
    useRebalancingPlanSidebarItemProps(cluster);
  const isOmniAvailableRegionsFeatureEnabled =
    useOmniAvailableRegionsFeatureEnabled();
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const { isInstalled: isSecAgentInstalled, isLoading: isSecAgentLoading } =
    useIsClusterSecurityAgentInstalled({
      clusterId: cluster.id,
      enabled: isSecuritySunsetEnabled,
    });
  const isSecAgentNotInstalledAndLoaded =
    !isSecAgentInstalled && !isSecAgentLoading;
  const { isVulnerabilityDetectionEnabled, isComplianceChecksEnabled } =
    useClusterSecurityFeatures({
      clusterId: cluster.id,
      enabled: isSecuritySunsetEnabled,
    });
  const { canViewClusterLevelStop } = useStorageOptimizationAbilities();
  const { limitAutomationConsole, showAutomationUpsell } = useConsoleAccess();
  const reportingOnlyNav = limitAutomationConsole && !showAutomationUpsell;
  const automationLocked = limitAutomationConsole;

  // Limited console: only Dashboard, Available savings, and Cost monitoring.
  if (reportingOnlyNav) {
    return (
      <>
        <SidebarItem
          title="Dashboard"
          url={`${basePath}/external-clusters/${cluster.id}/dashboard`}
        />
        {!isKentEligible && <SavingsSidebarItem cluster={cluster} />}
        <CostMonitoringClusterSidebarItems cluster={cluster} />
      </>
    );
  }

  if (automationLocked) {
    return (
      <>
        <SidebarItem
          title="Dashboard"
          url={`${basePath}/external-clusters/${cluster.id}/dashboard`}
        />
        <SidebarItem
          title="Cluster score"
          url={`${basePath}/external-clusters/${cluster.id}/cluster-score`}
          rules={[['view', 'ClusterScore']]}
          rulesConditions={{ clusterId: cluster.id }}
        />
        <SidebarItem
          title="Optimization constraints"
          url={`${basePath}/external-clusters/${cluster.id}/optimization-constraints`}
          rules={[['view', 'ClusterScoreRestrictions']]}
          rulesConditions={{ clusterId: cluster.id }}
          disabled={isDisconnected}
        />
        {!isKentEligible && <SavingsSidebarItem cluster={cluster} />}
        <CostMonitoringClusterSidebarItems cluster={cluster} />
      </>
    );
  }

  return (
    <>
      {isKentEligible && (
        <SidebarItem
          title="Get started"
          url={makeKentGetStartedLink({ clusterId: cluster.id })}
          rules={[['view', 'ClusterKentFeatures']]}
          rulesConditions={{ clusterId: cluster.id }}
        />
      )}

      <SidebarItem
        title="Dashboard"
        url={`${basePath}/external-clusters/${cluster.id}/dashboard`}
      />

      <SidebarItem
        title="Cluster score"
        url={`${basePath}/external-clusters/${cluster.id}/cluster-score`}
        rules={[['view', 'ClusterScore']]}
        rulesConditions={{ clusterId: cluster.id }}
      />
      <SidebarItem
        title="Optimization constraints"
        url={`${basePath}/external-clusters/${cluster.id}/optimization-constraints`}
        rules={[['view', 'ClusterScoreRestrictions']]}
        rulesConditions={{ clusterId: cluster.id }}
        disabled={isDisconnected}
      />

      {/* Karpenter cluster has "Get started" sidebar item, so we don't need to show the savings sidebar item */}
      {!isKentEligible && <SavingsSidebarItem cluster={cluster} />}

      <CostMonitoringClusterSidebarItems cluster={cluster} />

      <SidebarItem
        title="Workload autoscaler"
        url={`${basePath}/external-clusters/${cluster.id}/workload-optimization/workloads`}
        expanded={isOpTourActive}
      >
        <SidebarItem
          title="Optimization"
          testId="woop-dashboard"
          url={`${basePath}/external-clusters/${cluster.id}/workload-optimization/workloads`}
        />
        <SidebarItem
          title="Scaling policies"
          url={`${basePath}/external-clusters/${cluster.id}/workload-optimization/scaling-policies`}
        />
        <SidebarItem
          title="Impact"
          url={`${basePath}/external-clusters/${cluster.id}/workload-optimization/impact`}
          rules={[['view', 'ClusterWorkloadImpact']]}
        />
        <SidebarItem
          title="Custom metrics"
          url={`${basePath}/external-clusters/${cluster.id}/workload-optimization/custom-metrics`}
          rules={[['view', 'ClusterWorkloadCustomMetrics']]}
          endAdornment={<SidebarItemBadge>NEW</SidebarItemBadge>}
        />
        <SidebarItem
          title="Horizontal pod autoscalers"
          url={`${basePath}/external-clusters/${cluster.id}/workload-optimization/hpa`}
          rules={[['view', 'ClusterWorkloadHpaList']]}
          endAdornment={<SidebarItemBadge>NEW</SidebarItemBadge>}
        />
        <SidebarItem
          title="Event log"
          url={`${basePath}/external-clusters/${cluster.id}/workload-optimization/events`}
        />
      </SidebarItem>

      <SidebarItem
        title="Node autoscaler"
        url={`${basePath}/external-clusters/${cluster.id}/nodes`}
        expanded={isOpTourActive}
      >
        <SidebarItem
          title="Node list"
          url={`${basePath}/external-clusters/${cluster.id}/nodes`}
          rules={[['view', 'ClusterNodes']]}
          rulesConditions={{ clusterId: cluster.id }}
          disabled={isDisconnected}
        />
        {isOmniAvailableRegionsFeatureEnabled &&
          cluster.providerType !== K8sProvider.ANYWHERE && (
            <SidebarItem
              title="Global capacity"
              url={`${basePath}/external-clusters/${cluster.id}/autoscaler/global-capacity`}
            />
          )}
        {!isKentEligible && (
          <SidebarItem
            title="Configuration"
            url={makeNavigationLinkToAutoscalerSettings(cluster.id)}
          />
        )}
        {isKentEligible && (
          <SidebarItem
            title="Karpenter"
            url={makeKarpenterSettingsLink({ clusterId: cluster.id })}
            rules={[['view', 'ClusterKentKarpenterSettings']]}
            rulesConditions={{ clusterId: cluster.id }}
          />
        )}
        <SidebarItem
          title="Rebalancer"
          rules={[['view', 'ClusterRebalancer']]}
          rulesConditions={{ clusterId: cluster.id }}
          {...rebalancingPlanSidebarItemProps}
        />
        <SidebarItem
          title="Container live migration"
          url={containerLiveMigrationLink}
          rules={[['view', 'FeatureClusterContainerLiveMigration']]}
          rulesConditions={{ clusterId: cluster.id }}
        />
        <SidebarItem
          title="Spot interruptions"
          url={`${basePath}/external-clusters/${cluster.id}/spot-interruptions`}
          rules={[['view', 'FeatureClusterSpotInterruptions']]}
          rulesConditions={{ clusterId: cluster.id }}
        />
      </SidebarItem>

      {canViewClusterLevelStop && (
        <SidebarItem
          title="Storage autoscaler"
          url={`${basePath}/external-clusters/${cluster.id}/storage-autoscaler`}
          rulesConditions={{ clusterId: cluster.id }}
          endAdornment={<SidebarItemBadge>NEW</SidebarItemBadge>}
        >
          <SidebarItem
            title="Persistent volumes"
            url={`${basePath}/external-clusters/${cluster.id}/storage-autoscaler/persistent-volumes`}
            rules={[['view', 'ClusterStopPersistentVolumes']]}
            rulesConditions={{ clusterId: cluster.id }}
          />
          <SidebarItem
            title="Policies"
            url={`${basePath}/external-clusters/${cluster.id}/storage-autoscaler/policies`}
            rules={[['view', 'ClusterStopPvPolicies']]}
            rulesConditions={{ clusterId: cluster.id }}
          />
          <SidebarItem
            title="Events"
            url={`${basePath}/external-clusters/${cluster.id}/storage-autoscaler/events`}
            rules={[['view', 'ClusterStopEvents']]}
            rulesConditions={{ clusterId: cluster.id }}
          />
          <SidebarItem
            title="Configuration"
            url={`${basePath}/external-clusters/${cluster.id}/storage-autoscaler/configuration`}
            rules={[['view', 'ClusterStopConfig']]}
            rulesConditions={{ clusterId: cluster.id }}
          />
        </SidebarItem>
      )}

      {isSecuritySunsetEnabled &&
        (isVulnerabilityDetectionEnabled ||
          isSecAgentNotInstalledAndLoaded) && (
          <SidebarItem
            title="Vulnerabilities"
            url={`${basePath}/external-clusters/${cluster.id}/vulnerabilities`}
            rules={[['view', 'OrganizationSecurityVulnerabilities']]}
          />
        )}
      {isSecuritySunsetEnabled &&
        (isComplianceChecksEnabled || isSecAgentNotInstalledAndLoaded) && (
          <SidebarItem
            title="Compliance"
            url={`${basePath}/external-clusters/${cluster.id}/compliance`}
            rules={[['view', 'OrganizationSecurityCompliance']]}
          />
        )}

      {isKentEligible ? (
        <SidebarItem
          title="Settings"
          url={makeKentSettingsLink({ clusterId: cluster.id })}
          rules={[['view', 'ClusterKentFeatures']]}
          rulesConditions={{ clusterId: cluster.id }}
        />
      ) : (
        <SettingsSidebarItem cluster={cluster} />
      )}

      <SidebarItem
        title="Logs"
        testId="cluster-logs"
        url={`${basePath}/external-clusters/${cluster.id}/logs`}
        rules={[['view', 'ClusterAuditLog']]}
        rulesConditions={{ clusterId: cluster.id }}
      />
    </>
  );
};

export const AutomationSidebar = () => {
  const { anywhereOnly, clusters } = useClusters();
  const basePath = PRODUCT_BASE_PATHS.automation;
  const { limitAutomationConsole } = useConsoleAccess();

  const [canViewHibernate, _canViewSchedules] = useAbility().canMany([
    ['view', 'OrganizationClusterHibernate'],
    ['view', 'OrganizationRebalanceSchedules'],
  ]);

  const kentClustersOnly = clusters.every((c) => isKentManagedCluster(c));
  const canViewSchedules = _canViewSchedules && !kentClustersOnly;

  return (
    <SidebarLayout
      title={limitAutomationConsole ? 'Cost monitoring' : 'Automation'}
      subtitle={
        limitAutomationConsole ? 'Reporting' : 'Optimize infrastructure'
      }
      icon={
        <Icons.CastCostOptimization variant="preset" weight="light" size={24} />
      }
    >
      <SidebarOrgSection>
        <SidebarItem
          title="Cluster List"
          url={`${basePath}/cluster-list`}
          allowedInReadOnlyOrg
        />
        {!limitAutomationConsole && (
          <SidebarItem
            title="Overview"
            url={`${basePath}/overview`}
            rules={[
              ['view', 'ClusterAutomationNewDashboard'],
              ['view', 'OrganizationCostMonitoring'],
            ]}
            supportsDemo={false}
          />
        )}
        {!limitAutomationConsole && (
          <SidebarItem
            title="Cloud VMs"
            url={`${basePath}/cloud-vms`}
            allowedInReadOnlyOrg
          />
        )}
        {!limitAutomationConsole &&
          (() => {
            // TODO (Justas): Add such support into SidebarItem
            if (canViewHibernate && canViewSchedules) {
              return (
                <SidebarItem
                  title="Schedules"
                  url={`${basePath}/schedules`}
                  rules={[
                    ['view', 'OrganizationClusterHibernate'],
                    ['view', 'OrganizationRebalanceSchedules'],
                  ]}
                  supportsDemo={false}
                >
                  <SidebarItem
                    title="Hibernation"
                    url={`${basePath}/schedules/cluster-hibernation`}
                    rules={[['view', 'OrganizationClusterHibernate']]}
                    disabled={anywhereOnly}
                  />

                  <SidebarItem
                    title="Rebalancing"
                    url={`${basePath}/schedules/scheduled-rebalancing`}
                    rules={[['view', 'OrganizationRebalanceSchedules']]}
                    disabled={anywhereOnly}
                  />
                </SidebarItem>
              );
            }
            if (canViewHibernate) {
              return (
                <SidebarItem
                  title="Scheduled Hibernation"
                  url={`${basePath}/schedules/cluster-hibernation`}
                  rules={[['view', 'OrganizationClusterHibernate']]}
                  disabled={anywhereOnly}
                  supportsDemo={false}
                />
              );
            }
            if (canViewSchedules) {
              return (
                <SidebarItem
                  title="Schedules"
                  url={`${basePath}/schedules/scheduled-rebalancing`}
                  rules={[['view', 'OrganizationRebalanceSchedules']]}
                  disabled={anywhereOnly}
                  supportsDemo={false}
                />
              );
            }
            return undefined;
          })()}

        <CostMonitoringOrgSidebarItems />
      </SidebarOrgSection>
      <SidebarClusterSection
        defaultPathTemplate={`${basePath}/external-clusters/:clusterId/dashboard`}
      >
        {(cluster) => (
          // ClusterProvider is needed to ensure that rebalancing hooks that rely on cluster context work correctly
          <ClusterProvider cluster={cluster}>
            <ClusterAutomationSidebar cluster={cluster} />
          </ClusterProvider>
        )}
      </SidebarClusterSection>
    </SidebarLayout>
  );
};
